import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from 'usehooks-ts';
import {
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { useHollySession } from '@hooks';
import { EmailVerificationBanner } from '@components/EmailVerificationBanner';
import { PageHeader } from '../../tailwindComponents/PageHeader';

/**
 * Holly 3.0 Registration spec §6.3 — "Add your services" destination shown to
 * LSP-flow registrants. Two cards (warehouse profile, transport depot); the one
 * matching Holly's `declaredTrack` is primary-styled. Card click routes to the
 * existing creation flows so we don't duplicate form logic.
 */

type Track = 'warehousing' | 'transport' | 'both';

export function AddListing() {
  useDocumentTitle('FLOX - Add your services');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session } = useHollySession();

  const trackFromUrl = searchParams.get('track') as Track | null;
  const declaredTrack: Track =
    (session?.flowState?.declaredTrack as Track) ||
    (trackFromUrl && ['warehousing', 'transport', 'both'].includes(trackFromUrl)
      ? trackFromUrl
      : 'warehousing');

  const warehousePrimary = declaredTrack === 'warehousing' || declaredTrack === 'both';
  const transportPrimary = declaredTrack === 'transport' && !warehousePrimary;

  return (
    <>
      <PageHeader
        header='Add your services'
        pages={[
          { name: 'Listings', href: '/app/listings/add', current: false },
          { name: 'Add', href: '/app/listings/add', current: true },
        ]}
      />
      <main className='min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)]'>
        <div className='px-4 pt-4 lg:px-8 lg:pt-6'>
          <EmailVerificationBanner />
        </div>

        <div className='mx-auto max-w-6xl px-4 pb-12 pt-6 lg:px-8 lg:pt-10'>
          <div className='grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-14'>
            <section>
              <div className='mb-10 max-w-2xl'>
                <span className='inline-block text-[11px] font-semibold uppercase tracking-[0.18em] text-primary'>
                  Step 1 of 3
                </span>
                <h1 className='mt-3 text-3xl font-bold leading-tight tracking-tight text-floxBlue lg:text-4xl'>
                  Get your capacity in front of buyers.
                </h1>
                <p className='mt-3 text-base leading-relaxed text-gray-600'>
                  Add a warehouse or a transport depot to start. You can add more sites whenever you like.
                </p>
              </div>

              <div className='grid grid-cols-1 gap-5 md:grid-cols-2'>
                <ListingCard
                  Icon={BuildingOffice2Icon}
                  title='Warehouse profile'
                  description='List a warehouse location with its capacity, services, and sectors you serve.'
                  ctaLabel='Start with warehousing'
                  primary={warehousePrimary}
                  onClick={() => navigate('/warehouse-master')}
                />
                <ListingCard
                  Icon={TruckIcon}
                  title='Transport depot & fleet'
                  description='List a depot with your fleet, routes, and transport capabilities.'
                  ctaLabel='Add a transport depot'
                  primary={transportPrimary}
                  onClick={() => navigate('/fleet-master')}
                />
              </div>

              <p className='mt-6 text-sm text-gray-500'>
                Have multiple sites? You&apos;ll be able to add more once your first listing is live.
              </p>
            </section>

            <aside className='lg:pt-[3.25rem]'>
              <div className='rounded-2xl border border-gray-200 bg-white p-6'>
                <div className='mb-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500'>
                  Setup progress
                </div>
                <ol className='space-y-5'>
                  <Step number={1} label='Add your first listing' status='active' hint='In progress' />
                  <Step number={2} label='Upload supporting docs' status='upcoming' hint='Insurance, certifications' />
                  <Step number={3} label='Get verified' status='upcoming' hint='Go live on the marketplace' />
                </ol>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}

interface ListingCardProps {
  Icon: typeof BuildingOffice2Icon;
  title: string;
  description: string;
  ctaLabel: string;
  primary: boolean;
  onClick: () => void;
}

function ListingCard({ Icon, title, description, ctaLabel, primary, onClick }: ListingCardProps) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={[
        'group relative flex h-full flex-col items-start overflow-hidden rounded-2xl border bg-white p-7 text-left transition-all duration-200',
        'hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-25px_rgba(0,0,102,0.35)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        primary
          ? 'border-primary/40 ring-1 ring-primary/20 hover:border-primary'
          : 'border-gray-200 hover:border-gray-300',
      ].join(' ')}
    >
      {primary && (
        <>
          <span className='absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary to-floxBlue' />
          <span className='mb-4 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary'>
            <span className='h-1.5 w-1.5 rounded-full bg-primary' />
            Recommended
          </span>
        </>
      )}

      <div
        className={[
          'mb-5 flex h-12 w-12 items-center justify-center rounded-xl transition-colors',
          primary
            ? 'bg-floxBlue text-white'
            : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200',
        ].join(' ')}
      >
        <Icon className='h-6 w-6' strokeWidth={1.5} />
      </div>

      <h3 className='text-lg font-bold tracking-tight text-floxBlue'>{title}</h3>
      <p className='mt-2 text-sm leading-relaxed text-gray-600'>{description}</p>

      <div className='mt-auto pt-6'>
        <span
          className={[
            'inline-flex items-center gap-2 text-sm font-semibold transition-all',
            primary ? 'text-primary' : 'text-gray-700 group-hover:text-primary',
          ].join(' ')}
        >
          {ctaLabel}
          <ArrowRightIcon className='h-4 w-4 transition-transform duration-200 group-hover:translate-x-1' />
        </span>
      </div>
    </button>
  );
}

interface StepProps {
  number: number;
  label: string;
  hint: string;
  status: 'active' | 'upcoming' | 'done';
}

function Step({ number, label, hint, status }: StepProps) {
  const isActive = status === 'active';
  const isDone = status === 'done';
  const isUpcoming = status === 'upcoming';
  return (
    <li className='flex gap-3'>
      <div
        className={[
          'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold transition-colors',
          isActive && 'bg-primary text-white shadow-[0_0_0_4px_rgba(0,102,203,0.12)]',
          isDone && 'bg-emerald-50 text-emerald-600',
          isUpcoming && 'border border-gray-200 bg-white text-gray-400',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {isDone ? <CheckCircleIcon className='h-4 w-4' /> : number}
      </div>
      <div className={isUpcoming ? 'opacity-70' : ''}>
        <div className={`text-sm font-semibold ${isActive ? 'text-floxBlue' : 'text-gray-900'}`}>
          {label}
        </div>
        <div className='mt-0.5 text-xs text-gray-500'>{hint}</div>
      </div>
    </li>
  );
}
